import { Box, FormControl, InputLabel } from '@mui/material';
import Select from '@mui/material/Select';
import { packagedAppsStatusList } from './constants';
import MenuItem from '@mui/material/MenuItem';
import React, { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { USER_QUERY_KEY, WDAC_PACKAGED_APP_QUERY_KEY } from '../../../../constants/query';
import { pagesText } from '../../../../constants/pagesText';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../ConfirmDialog';
import useSubscribeStorage from '../../../../hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from '../../../../constants/storage';
import useQueryData from '../../../../queries/useQueryData';
import { userEmailSelector } from '../../../../selectors/user';
import WDACPackagedAppService from '../../../../services/wdac-packaged-app.service';
import { buildDefaultStatusNote } from 'src/features/appStatus/defaultStatusNote';

export const BulkStatusSelect = ({
  selectedIds,
  setSelectedIds,
  query,
  top = '32px',
  filteredCount,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const email = useQueryData(USER_QUERY_KEY, userEmailSelector);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const { mutate: bulkUpdatePackagedAppStatus, isLoading: statusLoading } = useMutation(
    WDACPackagedAppService.bulkUpdateGlobalPackagedAppStatus,
    {
      onSuccess: async () => {
        enqueueSnackbar('Statuses updated successfully.', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        setOpenConfirmModal(false);
        setSelectedIds([]);
        await queryClient.invalidateQueries([WDAC_PACKAGED_APP_QUERY_KEY, query, customerEnvId]);
      },
      onError: () => {
        enqueueSnackbar(pagesText.somethingWentWrong, { variant: 'error', autoHideDuration: 3000 });
      },
    },
  );

  const handleChangeStatus = useCallback(() => {
    bulkUpdatePackagedAppStatus({
      customerEnvId: customerEnvId,
      globalPackagedAppIds: selectedIds,
      status,
      notes: buildDefaultStatusNote(email),
    });
  }, [status, selectedIds, customerEnvId, bulkUpdatePackagedAppStatus, email]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top,
        right: '95px',
        width: '120px',
        minWidth: '120px',
        marginRight: !filteredCount ? 0 : '40px',
      }}>
      <FormControl fullWidth size="small">
        <InputLabel id="demo-simple-select-label">Status</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={status}
          label="Status"
          sx={{
            height: 35,
            fontSize: '13px',
            '.MuiSelect-select': {
              padding: '4px 8px',
              minHeight: 'unset',
            },
            '.MuiOutlinedInput-notchedOutline': {
              top: 0,
            },
            '.MuiSelect-icon': {
              top: 'calc(50% - 10px)',
            },
          }}
          onChange={e => {
            setOpenConfirmModal(true);
            setStatus(e.target.value);
          }}>
          {packagedAppsStatusList?.map(option => (
            <MenuItem value={option?.value}>{option?.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <ConfirmDialog
        statusLoading={statusLoading}
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        onSuccess={handleChangeStatus}
      />
    </Box>
  );
};
